import React, { useState, useCallback, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, KeyboardAvoidingView, Platform, TouchableOpacity, TextInput, Linking, Switch } from 'react-native';
import { useRouter, useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../src/context/AuthContext';
import { useToast } from '../../src/components/Toast';
import { api, saveToken } from '../../src/api/client';
import { openExternal } from '../../src/utils/openExternal';
import { SMS_UI_ENABLED, PUSH_UI_ENABLED } from '../../src/config/features';
import Button from '../../src/components/Button';
import InlineConfirm from '../../src/components/InlineConfirm';
import Input from '../../src/components/Input';
import Card from '../../src/components/Card';
import { useTheme } from '../../src/context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../../src/utils/theme';
import type { ColorTheme } from '../../src/utils/theme';
import { rs, fs } from '../../src/utils/responsive';
import { getTimezone, setTimezone, TIMEZONE_OPTIONS } from '../../src/utils/timezone';
import ContentContainer from '../../src/components/ContentContainer';
import Picker from '../../src/components/Picker';
import InlineMessage from '../../src/components/InlineMessage';
import { refreshActivationBanner } from '../../src/components/ActivationBanner';
import StripePrepModal from '../../src/components/StripePrepModal';

interface Profile {
  id: string;
  accountNumber: string;
  email: string;
  businessName?: string;
  phone?: string;
  timezone?: string;
}

/** OwnerSettingsScreen - profile, payments, security, appearance, and activation. */
export default function OwnerSettingsScreen() {
  const { colors, themeMode, setThemeMode } = useTheme();
  const styles = useStyles(colors);
  const { user, logout } = useAuth();
  const router = useRouter();
  const toast = useToast();

  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Edit states
  const [editingProfile, setEditingProfile] = useState(false);
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editBusinessName, setEditBusinessName] = useState('');
  const [saving, setSaving] = useState(false);

  // Password states
  const [changingPassword, setChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const fetchProfile = async () => {
    try {
      const data = await api<Profile & { twoFactorEnabled?: boolean }>('/api/auth/profile');
      setProfile(data);
      setEditEmail(data.email);
      setEditPhone(data.phone || '');
      setEditBusinessName(data.businessName || '');
      setTwoFactorEnabled(!!data.twoFactorEnabled);
      if (data.timezone) {
        setSelectedTz(data.timezone);
        setTimezone(data.timezone);
      }
    } catch (err) { console.error('fetch profile:', err); } finally { setLoading(false); }
  };

  useFocusEffect(useCallback(() => { fetchProfile(); }, []));

  // Sub-owner sessions: hide account-owner-only cards (server enforces too).
  const [isSubOwner, setIsSubOwner] = useState(false);
  useFocusEffect(useCallback(() => {
    api<{ isFullOwner: boolean }>('/api/me/sub-owner-permissions')
      .then(d => setIsSubOwner(!d.isFullOwner))
      .catch(() => {});
  }, []));

  // Alert delivery channels + text-usage summary
  const [alertPush, setAlertPush] = useState(true);
  const [alertSms, setAlertSms] = useState(false);
  const [alertHasPhone, setAlertHasPhone] = useState(false);
  const [smsUsage, setSmsUsage] = useState<{ unbilledCount: number; unbilledEstimatedCents: number } | null>(null);

  useFocusEffect(useCallback(() => {
    api<{ channels: { push?: boolean; sms?: boolean }; hasPhone: boolean }>('/api/owner/alert-channels')
      .then(d => { setAlertPush(d.channels.push !== false); setAlertSms(!!d.channels.sms); setAlertHasPhone(d.hasPhone); })
      .catch(() => {});
    api<{ unbilledCount: number; unbilledEstimatedCents: number }>('/api/owner/sms-usage')
      .then(d => setSmsUsage(d))
      .catch(() => {});
  }, []));

  const saveAlertChannels = async (push: boolean, sms: boolean) => {
    const prev = { push: alertPush, sms: alertSms };
    setAlertPush(push); setAlertSms(sms);
    try {
      await api('/api/owner/alert-channels', { method: 'PUT', body: { push, sms } });
    } catch (err: any) {
      setAlertPush(prev.push); setAlertSms(prev.sms);
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const body: any = {};
      if (editEmail !== profile?.email) body.email = editEmail;
      if (editPhone !== (profile?.phone || '')) body.phone = editPhone;
      if (editBusinessName !== (profile?.businessName || '')) body.businessName = editBusinessName;

      if (Object.keys(body).length === 0) {
        setEditingProfile(false);
        return;
      }

      const res = await api<{ success: boolean; token: string; profile: Profile }>('/api/auth/profile', {
        method: 'PUT',
        body,
      });
      if (res.token) await saveToken(res.token);
      setProfile(res.profile);
      setEditingProfile(false);
      toast.show({ type: 'success', title: 'Profile updated' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 8) {
      toast.show({ type: 'error', title: 'Password must be at least 8 characters' }); return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
      toast.show({ type: 'error', title: 'Password must include upper, lower, number, and special character' }); return;
    }
    if (newPassword !== confirmPassword) {
      toast.show({ type: 'error', title: 'Passwords do not match' }); return;
      return;
    }
    setSavingPassword(true);
    try {
      await api('/api/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      setChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.show({ type: 'success', title: 'Password changed successfully' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSavingPassword(false); }
  };

  const [selectedTz, setSelectedTz] = useState(getTimezone());

  // Activation state
  const [activationStatus, setActivationStatus] = useState<{ activated: boolean; status: string; cancelAtPeriodEnd?: boolean; waiverType?: string; waiverMonthsRemaining?: number; currentPeriodEnd?: string; gracePeriodEndsAt?: string } | null>(null);
  const [activating, setActivating] = useState(false);
  const [deactivating2, setDeactivating2] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const fetchActivation = async () => {
    try {
      const data = await api<any>('/api/owner/activation/status');
      setActivationStatus(data);
    } catch (err) { console.error('fetch activation status:', err); }
  };

  useFocusEffect(useCallback(() => { fetchActivation(); }, []));

  const handleActivate = async () => {
    setActivating(true);
    try {
      await api('/api/owner/activation/activate', { method: 'POST', body: {} });
      toast.show({ type: 'success', title: 'Account activated!' });
      fetchActivation();
      refreshActivationBanner();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Activation failed', message: err.message });
    } finally { setActivating(false); }
  };

  const handleDeactivate = async () => {
    setDeactivating2(true);
    try {
      const res = await api<any>('/api/owner/activation/deactivate', { method: 'POST', body: {} });
      const endDate = res.currentPeriodEnd ? new Date(res.currentPeriodEnd).toLocaleDateString() : '';
      toast.show({ type: 'success', title: 'Billing cancelled', message: endDate ? `Active until ${endDate}` : undefined });
      fetchActivation();
      refreshActivationBanner();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setDeactivating2(false); }
  };

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api('/api/owner/activation/reactivate', { method: 'POST', body: {} });
      toast.show({ type: 'success', title: 'Subscription reactivated' });
      fetchActivation();
      refreshActivationBanner();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setReactivating(false); }
  };

  const [stripeStatus, setStripeStatus] = useState<{ onboarded: boolean; chargesEnabled?: boolean; payoutsEnabled?: boolean } | null>(null);

  const fetchStripeStatus = async () => {
    try {
      const status = await api<{ onboarded: boolean; chargesEnabled?: boolean; payoutsEnabled?: boolean }>('/api/stripe/connect/status');
      setStripeStatus(status);
    } catch (err) { console.error('fetch stripe status:', err); }
  };

  useFocusEffect(useCallback(() => { fetchStripeStatus(); }, []));

  // Re-fetch Stripe status when user returns from Stripe tab
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const handler = () => {
      if (document.visibilityState === 'visible') fetchStripeStatus();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Check for ?stripe=complete query param and show feedback (web only)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'complete') {
      toast.show({ type: 'info', title: 'Checking Stripe status...' });
      fetchStripeStatus().then(() => {
        if (Platform.OS === 'web') window.history.replaceState({}, '', window.location.pathname);
      });
    } else if (params.get('stripe') === 'refresh') {
      toast.show({ type: 'warning', title: 'Stripe setup incomplete', message: 'Please try again.' });
      if (Platform.OS === 'web') window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [stripePrepMode, setStripePrepMode] = useState<'pre-stripe' | 'manage' | null>(null);

  const handleStripeConnect = () => {
    setStripePrepMode('pre-stripe');
  };

  const handleOpenVendorPage = () => {
    setStripePrepMode('manage');
  };

  const startStripeOnboarding = async () => {
    setStripePrepMode(null);
    try {
      const res = await api<{ url: string }>('/api/stripe/connect/onboard', { method: 'POST', body: {} });
      if (Platform.OS === 'web') {
        // Same-tab redirect on purpose: Stripe onboarding returns to our
        // return_url in this tab. Do not switch to openExternal/_blank here.
        window.location.href = res.url;
      } else {
        await openExternal(res.url);
      }
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  const handleStripeDashboard = async () => {
    try {
      const res = await api<{ url: string }>('/api/stripe/connect/dashboard-link', { method: 'POST', body: {} });
      await openExternal(res.url);
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    }
  };

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [toggling2FA, setToggling2FA] = useState(false);
  const [show2FACode, setShow2FACode] = useState(false);
  const [tfaCode, setTfaCode] = useState('');

  // Feedback state
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('praise');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [sendingFeedback, setSendingFeedback] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  // Delete account state
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleSubmitFeedback = async () => {
    if (!feedbackMessage.trim()) {
      toast.show({ type: 'error', title: 'Please enter a message' }); return;
    }
    setSendingFeedback(true);
    try {
      await api('/api/feedback', {
        method: 'POST',
        body: { type: feedbackType, message: feedbackMessage, rating: feedbackRating || undefined, screen: 'settings' },
      });
      setShowFeedback(false);
      setFeedbackMessage('');
      setFeedbackRating(0);
      toast.show({ type: 'success', title: 'Feedback sent!', message: 'Thank you for your feedback.' });
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Error', message: err.message });
    } finally { setSendingFeedback(false); }
  };

  const handleToggle2FA = async () => {
    if (twoFactorEnabled) {
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: false } });
        setTwoFactorEnabled(false);
        toast.show({ type: 'success', title: '2FA disabled' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    } else if (!show2FACode) {
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: true } });
        setShow2FACode(true);
        toast.show({ type: 'info', title: 'Code sent to your email' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    } else {
      if (tfaCode.length !== 6) { toast.show({ type: 'error', title: 'Enter 6-digit code' }); return; }
      setToggling2FA(true);
      try {
        await api('/api/auth/toggle-2fa', { method: 'POST', body: { enable: true, code: tfaCode } });
        setTwoFactorEnabled(true);
        setShow2FACode(false);
        setTfaCode('');
        toast.show({ type: 'success', title: '2FA enabled' });
      } catch (err: any) { toast.show({ type: 'error', title: 'Error', message: err.message }); }
      finally { setToggling2FA(false); }
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { setDeleteError('Please enter your password'); return; }
    setDeleteError('');
    setDeletingAccount(true);
    try {
      await api('/api/privacy/account?confirm=yes', { method: 'DELETE', body: { password: deletePassword } });
      toast.show({ type: 'success', title: 'Account deleted' });
      logout();
    } catch (err: any) {
      setDeleteError(err.message || 'Failed to delete account');
    } finally { setDeletingAccount(false); }
  };

  const handleLogout = () => {
    setConfirmLogout(true);
  };

  return (
    <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        <ContentContainer maxWidth={720}>
        {/* Profile Card */}
        <Card style={styles.profileCard}>
          {profile?.accountNumber && (
            <View style={styles.accountRow}>
              <Text style={styles.accountLabel}>Account:</Text>
              <Text style={styles.accountNumber}>{profile.accountNumber}</Text>
            </View>
          )}
          <View style={styles.avatar}>
            <Ionicons name="business" size={32} color={colors.primary} />
          </View>
          <Text style={styles.name}>{profile?.businessName || user?.businessName || user?.email}</Text>
          <Text style={styles.email}>{profile?.email || user?.email}</Text>
          {profile?.phone ? <Text style={styles.phone}>{profile.phone}</Text> : null}
        </Card>

        {/* Edit Profile */}
        {!editingProfile ? (
          <Button
            title="Edit Profile"
            onPress={() => setEditingProfile(true)}
            variant="outline"
            icon={<Ionicons name="create-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Edit Profile</Text>
            <Input label="Business Name" icon="business-outline" value={editBusinessName} onChangeText={setEditBusinessName} />
            <Input label="Email" icon="mail-outline" value={editEmail} onChangeText={setEditEmail} keyboardType="email-address" autoCapitalize="none" maxLength={255} />
            <Input label="Phone" icon="call-outline" value={editPhone} onChangeText={setEditPhone} keyboardType="phone-pad" />
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => setEditingProfile(false)} variant="ghost" style={{ flex: 1 }} />
              <Button title="Save" onPress={handleSaveProfile} loading={saving} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Change Password */}
        {!changingPassword ? (
          <Button
            title="Change Password"
            onPress={() => setChangingPassword(true)}
            variant="outline"
            icon={<Ionicons name="lock-closed-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Change Password</Text>
            <Input label="Current Password" icon="lock-closed-outline" value={currentPassword} onChangeText={setCurrentPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />
            <Input label="New Password" icon="lock-open-outline" value={newPassword} onChangeText={setNewPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} placeholder="Min. 8 characters" />
            <Input label="Confirm New Password" icon="lock-open-outline" value={confirmPassword} onChangeText={setConfirmPassword} secureTextEntry autoCapitalize="none" autoCorrect={false} />
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setChangingPassword(false); setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Update" onPress={handleChangePassword} loading={savingPassword} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Payments */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Deposit Account</Text>
          {stripeStatus?.onboarded ? (
            <>
              <View style={styles.stripeStatusRow}>
                <Ionicons name="checkmark-circle" size={20} color={colors.success} />
                <Text style={styles.stripeStatusText}>Stripe Connected</Text>
              </View>
              <Button
                title="Open Stripe Dashboard"
                onPress={handleStripeDashboard}
                variant="outline"
                icon={<Ionicons name="open-outline" size={18} color={colors.primary} />}
                style={{ marginTop: spacing.sm }}
              />
            </>
          ) : (
            <>
              <View style={styles.stripeStatusRow}>
                <Ionicons name="alert-circle" size={20} color={colors.warning} />
                <Text style={styles.stripeStatusText}>Stripe not connected</Text>
              </View>
              <Button
                title="Set Up Stripe Payouts"
                onPress={handleStripeConnect}
                variant="outline"
                icon={<Ionicons name="card" size={20} color={colors.primary} />}
                style={{ marginTop: spacing.sm }}
              />
            </>
          )}
          <Button
            title="KYC Page and Policies"
            onPress={handleOpenVendorPage}
            variant="outline"
            icon={<Ionicons name="globe-outline" size={18} color={colors.primary} />}
            style={{ marginTop: spacing.sm }}
          />
        </Card>

        {/* Account Activation */}
        {!isSubOwner && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Account Activation</Text>
          {activationStatus ? (
            <>
              <View style={styles.stripeStatusRow}>
                <Ionicons
                  name={activationStatus.activated ? 'checkmark-circle' : 'alert-circle'}
                  size={20}
                  color={activationStatus.activated ? colors.success : (activationStatus.status === 'grace_period' ? colors.warning : colors.danger)}
                />
                <Text style={styles.stripeStatusText}>
                  {activationStatus.status === 'active' ? 'Active' :
                   activationStatus.status === 'grace_period' ? 'Grace Period' : 'Inactive'}
                  {activationStatus.waiverType ? ` (Waived${activationStatus.waiverType === 'months' ? ` — ${activationStatus.waiverMonthsRemaining} mo remaining` : ' — indefinite'})` : ''}
                </Text>
              </View>
              {activationStatus.status === 'grace_period' && activationStatus.gracePeriodEndsAt && (
                <Text style={{ fontSize: fs(13), color: colors.warning, marginTop: spacing.xs }}>
                  Payment failed. Update your card by {new Date(activationStatus.gracePeriodEndsAt).toLocaleDateString()} to avoid deactivation.
                </Text>
              )}
              {!activationStatus.activated && (
                <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: spacing.xs, marginBottom: spacing.sm }}>
                  Activate your account ($100/month) so customers can purchase washes at your locations.
                </Text>
              )}
              {activationStatus.cancelAtPeriodEnd && activationStatus.currentPeriodEnd && (() => {
                const endDate = new Date(activationStatus.currentPeriodEnd);
                const now = new Date();
                const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
                return (
                  <View style={{ marginTop: spacing.sm, padding: spacing.md, borderRadius: borderRadius.sm, backgroundColor: colors.warning + '12', borderWidth: 1, borderColor: colors.warning + '30' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
                      <Ionicons name="time-outline" size={18} color={colors.warning} />
                      <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.warning }}>
                        Subscription ending
                      </Text>
                    </View>
                    <Text style={{ fontSize: fs(13), color: colors.text, marginTop: spacing.xs }}>
                      Active until {endDate.toLocaleDateString()} ({daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining)
                    </Text>
                    <View style={{ marginTop: spacing.xs, height: 6, borderRadius: 3, backgroundColor: colors.warning + '20', overflow: 'hidden' }}>
                      <View style={{ height: '100%', borderRadius: 3, backgroundColor: colors.warning, width: `${Math.max(5, Math.min(100, (daysLeft / 30) * 100))}%` }} />
                    </View>
                  </View>
                );
              })()}
              {activationStatus.currentPeriodEnd && activationStatus.activated && !activationStatus.waiverType && !activationStatus.cancelAtPeriodEnd && (
                <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: spacing.xs }}>
                  Next billing: {new Date(activationStatus.currentPeriodEnd).toLocaleDateString()}
                </Text>
              )}
              <View style={{ marginTop: spacing.sm }}>
                {!activationStatus.activated ? (
                  <Button title="Activate Account — $100/mo" onPress={handleActivate} loading={activating} />
                ) : activationStatus.cancelAtPeriodEnd ? (
                  <Button title="Reactivate Subscription" onPress={handleReactivate} loading={reactivating} size="sm" />
                ) : (
                  <>
                    <Button title="Deactivate" onPress={() => setConfirmDeactivate(true)} variant="danger" size="sm" />
                    <InlineConfirm
                      visible={confirmDeactivate}
                      message={activationStatus.currentPeriodEnd
                        ? `Your account will remain active until ${new Date(activationStatus.currentPeriodEnd).toLocaleDateString()}. You can reactivate anytime before then.`
                        : 'Are you sure you want to deactivate your account?'}
                      confirmLabel="Deactivate"
                      onConfirm={() => { setConfirmDeactivate(false); handleDeactivate(); }}
                      onCancel={() => setConfirmDeactivate(false)}
                      loading={deactivating2}
                      variant="danger"
                    />
                  </>
                )}
              </View>
            </>
          ) : (
            <Text style={{ fontSize: fs(13), color: colors.textSecondary }}>Loading...</Text>
          )}
        </Card>
        )}

        {/* Fee Info */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Platform Fees</Text>
          <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, lineHeight: 20 }}>
            RinsePay charges a 1% platform fee on all transactions. Stripe payment processing fees (2.9% + $0.30) also apply per transaction.
          </Text>
          <TouchableOpacity onPress={() => router.push('/terms')} style={{ marginTop: spacing.sm }}>
            <Text style={{ fontSize: fontSize.sm, color: colors.primary, fontWeight: fontWeight.medium }}>View full Terms of Service</Text>
          </TouchableOpacity>
        </Card>

        {/* Customer Payment Methods (separate from Stripe Connect payouts) */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>My Payment Methods</Text>
          <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            For using washes at other locations. Separate from your payout account.
          </Text>
          <Button
            title="Manage Cards"
            onPress={() => router.push('/payment-methods')}
            variant="outline"
            icon={<Ionicons name="card-outline" size={20} color={colors.primary} />}
          />
        </Card>

        {/* API access (1.4 P28): the only owner-side place keys are generated */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>API Access</Text>
          <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Generate keys for your own software to read your Rinsepay data. Every /api/v1 call needs a key from here.
          </Text>
          <Button
            title="API Access"
            onPress={() => router.push('/owner/api-access' as never)}
            variant="outline"
            icon={<Ionicons name="key-outline" size={20} color={colors.primary} />}
          />
        </Card>

        {/* Email alerts (1.4 P03): per-alert toggles + master switch */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Email Alerts</Text>
          <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Pick which alerts about your business arrive by email: wash failures, controllers offline, problem reports, staff notices, fleet requests, billing and scheduled reports.
          </Text>
          <Button
            title="Email Alerts"
            onPress={() => router.push('/owner/email-alerts' as never)}
            variant="outline"
            icon={<Ionicons name="mail-outline" size={20} color={colors.primary} />}
          />
        </Card>

        {/* Alert delivery (hidden until push/SMS delivery is activated) */}
        {(PUSH_UI_ENABLED || SMS_UI_ENABLED) && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Alert Delivery</Text>
          <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            How you receive alerts like controller offline, wash failures, and fleet applications. Choose which ones arrive by email under Email alerts.
          </Text>
          {PUSH_UI_ENABLED && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 }}>
            <Text style={{ fontSize: fs(14), color: colors.text }}>Push notifications</Text>
            <Switch
              value={alertPush}
              onValueChange={(v) => saveAlertChannels(v, alertSms)}
              trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
              thumbColor={alertPush ? colors.primary : colors.textLight}
            />
          </View>
          )}
          {SMS_UI_ENABLED && (
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingVertical: 6 }}>
            <View style={{ flex: 1, paddingRight: spacing.sm }}>
              <Text style={{ fontSize: fs(14), color: colors.text }}>Text messages{alertHasPhone ? '' : ' (add a phone number first)'}</Text>
              <Text style={{ fontSize: fs(12), color: colors.warning }}>
                Additional charge: ~1¢ per text, billed weekly at actual carrier cost.
              </Text>
            </View>
            <Switch
              value={alertSms}
              disabled={!alertHasPhone}
              onValueChange={(v) => saveAlertChannels(alertPush, v)}
              trackColor={{ false: colors.borderLight, true: colors.primary + '80' }}
              thumbColor={alertSms ? colors.primary : colors.textLight}
            />
          </View>
          )}
          {SMS_UI_ENABLED && smsUsage && (smsUsage.unbilledCount > 0 || smsUsage.unbilledEstimatedCents > 0) && (
            <Text style={{ fontSize: fs(12), color: colors.textSecondary, marginTop: spacing.xs }}>
              Text usage awaiting billing: {smsUsage.unbilledCount} message{smsUsage.unbilledCount !== 1 ? 's' : ''} (~${(smsUsage.unbilledEstimatedCents / 100).toFixed(2)}). Charged to your card on file each Monday; amounts under $0.50 roll forward.
            </Text>
          )}
        </Card>
        )}

        {/* Sub-owners (account owner only) */}
        {!isSubOwner && (
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Sub-Owners</Text>
          <Text style={{ fontSize: fs(13), color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Give partners or managers their own login with exactly the access you choose.
          </Text>
          <Button
            title="Manage Sub-Owners"
            onPress={() => router.push('/owner/sub-owners')}
            variant="outline"
            icon={<Ionicons name="people-outline" size={20} color={colors.primary} />}
          />
        </Card>

        )}

        {/* Timezone */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Timezone</Text>
          <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
            Used for displaying transaction dates and times.
          </Text>
          <Picker
            options={TIMEZONE_OPTIONS}
            value={selectedTz}
            onValueChange={async (val) => {
              setSelectedTz(val);
              setTimezone(val);
              try {
                await api('/api/auth/profile', { method: 'PUT', body: { timezone: val } });
                toast.show({ type: 'success', title: 'Timezone updated' });
              } catch {
                toast.show({ type: 'error', title: 'Failed to save timezone' });
              }
            }}
          />
        </Card>

        {/* Appearance */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Appearance</Text>
          <View style={{ flexDirection: 'row', gap: spacing.sm }}>
            {(['light', 'dark', 'system'] as const).map(mode => (
              <TouchableOpacity
                key={mode}
                onPress={() => setThemeMode(mode)}
                style={[styles.fbChip, themeMode === mode && styles.fbChipActive]}
              >
                <Text style={[styles.fbChipText, themeMode === mode && { color: colors.white }]}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </Card>

        {/* Security — 2FA */}
        <Card style={styles.section}>
          <Text style={styles.sectionTitle}>Security</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.text }}>Two-Factor Authentication</Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: 2 }}>
                {twoFactorEnabled ? 'Enabled — login code sent via email' : 'Add extra security to your account'}
              </Text>
            </View>
            <Button
              title={twoFactorEnabled ? 'Disable' : 'Enable'}
              onPress={handleToggle2FA}
              loading={toggling2FA}
              variant={twoFactorEnabled ? 'danger' : 'outline'}
              size="sm"
            />
          </View>
          {show2FACode && (
            <View style={{ marginTop: spacing.sm }}>
              <Input label="Enter 6-digit code from email" icon="key-outline" value={tfaCode} onChangeText={setTfaCode} keyboardType="number-pad" maxLength={6} />
              <Button title="Confirm" onPress={handleToggle2FA} loading={toggling2FA} style={{ marginTop: spacing.xs }} />
            </View>
          )}
        </Card>

        {/* Support */}
        <Button
          title="support@rinsepay.app"
          onPress={() => Linking.openURL('mailto:support@rinsepay.app')}
          variant="ghost"
          icon={<Ionicons name="mail-outline" size={20} color={colors.textSecondary} />}
          style={[styles.actionBtn, { borderWidth: 1, borderColor: colors.primary + '40', borderRadius: borderRadius.md }]}
        />
        {/* Maintenance window (Nathan 2026-10-16): production updates run 11:00 PM to 1:00 AM Central. Keep this quiet. */}
        <Text style={{ fontSize: fontSize.xs, color: colors.textLight, textAlign: 'center', marginTop: spacing.xs, marginBottom: spacing.sm }}>
          Scheduled maintenance: 11:00 PM to 1:00 AM Central. Payments may pause briefly during that hour.
        </Text>

        {/* Feedback */}
        {!showFeedback ? (
          <Button
            title="Send Feedback"
            onPress={() => setShowFeedback(true)}
            variant="outline"
            icon={<Ionicons name="chatbubble-ellipses-outline" size={20} color={colors.primary} />}
            style={styles.actionBtn}
          />
        ) : (
          <Card style={styles.editCard}>
            <Text style={styles.sectionTitle}>Send Feedback</Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm }}>
              Help us improve! Let us know what you think.
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs, marginBottom: spacing.sm }}>
              {['praise', 'feature', 'bug', 'complaint'].map(t => (
                <TouchableOpacity key={t} onPress={() => setFeedbackType(t)}
                  style={[styles.fbChip, feedbackType === t && styles.fbChipActive]}>
                  <Text style={[styles.fbChipText, feedbackType === t && { color: colors.white }]}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.fbInput}
              placeholder="Tell us more..."
              placeholderTextColor={colors.textLight}
              value={feedbackMessage}
              onChangeText={setFeedbackMessage}
              multiline
              numberOfLines={3}
              maxLength={500}
            />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginTop: spacing.sm }}>
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>Rating:</Text>
              {[1, 2, 3, 4, 5].map(n => (
                <TouchableOpacity key={n} onPress={() => setFeedbackRating(n)}>
                  <Ionicons name={n <= feedbackRating ? 'star' : 'star-outline'} size={22} color={n <= feedbackRating ? '#f59e0b' : colors.textLight} />
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setShowFeedback(false); setFeedbackMessage(''); setFeedbackRating(0); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Send" onPress={handleSubmitFeedback} loading={sendingFeedback} style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        {/* Delete Account */}
        {!showDeleteAccount ? (
          <Button
            title="Delete Account"
            onPress={() => setShowDeleteAccount(true)}
            variant="ghost"
            icon={<Ionicons name="trash-outline" size={20} color={colors.danger} />}
            style={[styles.actionBtn, { borderWidth: 1, borderColor: colors.danger + '40', borderRadius: borderRadius.md }]}
          />
        ) : (
          <Card style={[styles.editCard, { borderColor: colors.danger + '40', borderWidth: 1 }]}>
            <Text style={styles.sectionTitle}>Delete Account</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.danger, marginTop: -spacing.sm, marginBottom: spacing.sm, lineHeight: 20 }}>
              This will permanently delete your account and all associated data. This action cannot be undone.
            </Text>
            <Input label="Enter your password to confirm" icon="lock-closed-outline" value={deletePassword} onChangeText={setDeletePassword} secureTextEntry />
            {deleteError ? <Text style={styles.error}>{deleteError}</Text> : null}
            <View style={styles.btnRow}>
              <Button title="Cancel" onPress={() => { setShowDeleteAccount(false); setDeletePassword(''); setDeleteError(''); }} variant="ghost" style={{ flex: 1 }} />
              <Button title="Delete Account" onPress={handleDeleteAccount} loading={deletingAccount} variant="danger" style={{ flex: 1 }} />
            </View>
          </Card>
        )}

        <Button title="Sign Out" onPress={handleLogout} variant="danger" style={styles.logoutBtn} />
        {confirmLogout && (
          <InlineConfirm
            message="Are you sure you want to sign out?"
            visible={true}
            onConfirm={() => {
              setConfirmLogout(false);
              logout();
            }}
            onCancel={() => setConfirmLogout(false)}
            confirmLabel="Sign Out"
            variant="danger"
          />
        )}
        <View style={{ height: spacing.xxl }} />
        </ContentContainer>
      </ScrollView>
      {profile?.accountNumber && stripePrepMode && (
        <StripePrepModal
          visible
          accountNumber={profile.accountNumber}
          mode={stripePrepMode}
          onClose={() => setStripePrepMode(null)}
          onContinue={startStripeOnboarding}
        />
      )}
    </KeyboardAvoidingView>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md },
  profileCard: { alignItems: 'center', marginBottom: spacing.md },
  avatar: {
    width: rs(64), height: rs(64), borderRadius: rs(32),
    backgroundColor: c.primaryBg,
    justifyContent: 'center', alignItems: 'center', marginBottom: spacing.md,
  },
  name: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  email: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: spacing.xs },
  phone: { fontSize: fontSize.sm, color: c.textLight, marginTop: 2 },
  accountRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.md, backgroundColor: c.primaryBg, paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: borderRadius.md, alignSelf: 'center' },
  accountLabel: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.textSecondary },
  accountNumber: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.primaryDark, fontFamily: 'monospace' },
  actionBtn: { marginBottom: spacing.md },
  error: { fontSize: fontSize.sm, color: c.danger, backgroundColor: c.danger + '10', padding: spacing.md, borderRadius: borderRadius.sm, marginBottom: spacing.sm, textAlign: 'center' },
  editCard: { marginBottom: spacing.md },
  sectionTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  btnRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  section: { marginBottom: spacing.md },
  stripeStatusRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  stripeStatusText: { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.text },
  logoutBtn: { marginTop: spacing.lg },
  fbChip: { paddingHorizontal: rs(14), paddingVertical: rs(6), borderRadius: 16, backgroundColor: c.surfaceSecondary, borderWidth: 1, borderColor: c.border },
  fbChipActive: { backgroundColor: c.primary, borderColor: c.primary },
  fbChipText: { fontSize: fontSize.xs, color: c.textSecondary, fontWeight: fontWeight.medium },
  fbInput: { backgroundColor: c.surfaceSecondary, borderRadius: borderRadius.sm, padding: spacing.sm, fontSize: fontSize.sm, color: c.text, borderWidth: 1, borderColor: c.border, minHeight: rs(80), textAlignVertical: 'top' },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
